import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchSelect } from "@/components/SearchSelect";
import { api, euro, prezzoFinale, scontoLabel } from "@/lib/api";

const num = (v) => (v === "" ? "" : Number(v));
const Cell = ({ label, children, className = "" }) => (
  <div className={className}><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 sm:hidden">{label}</p>{children}</div>
);

export const RicambiRows = ({ rows, setRows, magazzino }) => {
  const opts = magazzino.map((r) => ({ value: r.id, label: `${r.nome}${r.codice ? ` (${r.codice})` : ""}`, hint: `disp. ${r.quantita} · ${euro(prezzoFinale(r))}${r.sconto ? ` (${scontoLabel(r)})` : ""}` }));
  const add = (id) => {
    const r = magazzino.find((x) => x.id === id);
    const i = rows.findIndex((x) => x.ricambio_id === id);
    if (i >= 0) return setRows(rows.map((x, k) => (k === i ? { ...x, quantita: Number(x.quantita) + 1 } : x)));
    setRows([...rows, { ricambio_id: r.id, nome: r.nome, codice: r.codice, quantita: 1, prezzo: prezzoFinale(r) }]);
  };
  const upd = (i, k, v) => setRows(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid="ricambi-rows">
      {rows.map((r, i) => (
        <div key={r.ricambio_id} className="grid grid-cols-2 items-end gap-2 rounded-md border border-slate-100 bg-slate-50/60 p-2 sm:grid-cols-[1fr_80px_110px_110px_40px] sm:items-center" data-testid={`ricambio-row-${i}`}>
          <Cell label="Ricambio" className="col-span-2 sm:col-span-1"><p className="text-sm font-medium text-slate-900">{r.nome} <span className="font-mono text-xs text-slate-400">{r.codice}</span></p></Cell>
          <Cell label="Qtà"><Input type="number" min="1" value={r.quantita} onChange={(e) => upd(i, "quantita", num(e.target.value))} data-testid={`ricambio-row-qty-${i}`} className="bg-white" /></Cell>
          <Cell label="Prezzo €"><Input type="number" step="0.01" value={r.prezzo} onChange={(e) => upd(i, "prezzo", num(e.target.value))} data-testid={`ricambio-row-price-${i}`} className="bg-white" /></Cell>
          <Cell label="Totale"><p className="font-mono text-sm font-bold sm:text-right">{euro((r.quantita || 0) * (r.prezzo || 0))}</p></Cell>
          <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`ricambio-row-remove-${i}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <SearchSelect options={opts} value="" onChange={add} placeholder="+ Aggiungi ricambio dal magazzino..." testId="add-ricambio-row-button" emptyText="Nessun ricambio in magazzino" />
    </div>
  );
};

const useLavoriFissi = () => {
  const [lavori, setLavori] = useState([]);
  useEffect(() => { api.get("/lavori_fissi").then((r) => setLavori(r.data)).catch(() => {}); }, []);
  return lavori;
};

export const ManodoperaRows = ({ rows, setRows, tariffa }) => {
  const lavori = useLavoriFissi();
  const upd = (i, k, v) => setRows(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const addFisso = (id) => {
    const l = lavori.find((x) => x.id === id);
    setRows([...rows, { descrizione: l.nome, ore: 1, tariffa: l.prezzo, fisso: true, ore_lavoro: l.durata_ore || 0 }]);
  };
  return (
    <div className="space-y-2" data-testid="manodopera-rows">
      {rows.map((m, i) => (
        <div key={i} className="grid grid-cols-2 items-end gap-2 rounded-md border border-slate-100 bg-slate-50/60 p-2 sm:grid-cols-[1fr_80px_110px_110px_40px] sm:items-center" data-testid={`manodopera-row-${i}`}>
          <Cell label="Descrizione" className="col-span-2 sm:col-span-1"><Input value={m.descrizione} onChange={(e) => upd(i, "descrizione", e.target.value)} placeholder="Es. Sostituzione catena e regolazione cambio" data-testid={`manodopera-row-desc-${i}`} className="bg-white" /></Cell>
          {m.fisso ? (
            <Cell label="Tipo"><p className="rounded-sm bg-orange-50 px-1.5 py-1 text-center text-[11px] font-bold uppercase tracking-wider text-orange-700" data-testid={`manodopera-row-fisso-${i}`}>Fisso</p></Cell>
          ) : (
            <Cell label="Ore"><Input type="number" step="0.25" value={m.ore} onChange={(e) => upd(i, "ore", num(e.target.value))} data-testid={`manodopera-row-ore-${i}`} className="bg-white" /></Cell>
          )}
          <Cell label={m.fisso ? "Prezzo €" : "€/ora"}><Input type="number" step="0.01" value={m.tariffa} onChange={(e) => upd(i, "tariffa", num(e.target.value))} data-testid={`manodopera-row-tariffa-${i}`} className="bg-white" /></Cell>
          <Cell label="Totale"><p className="font-mono text-sm font-bold sm:text-right">{euro((m.ore || 0) * (m.tariffa || 0))}</p></Cell>
          <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`manodopera-row-remove-${i}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <div className="grid gap-2 sm:grid-cols-2">
        <SearchSelect options={lavori.map((l) => ({ value: l.id, label: l.nome, hint: euro(l.prezzo) }))} value="" onChange={addFisso}
          placeholder="+ Lavoro a prezzo fisso..." testId="add-lavoro-fisso-button" emptyText="Nessun lavoro: crealo in Impostazioni" />
        <Button variant="outline" onClick={() => setRows([...rows, { descrizione: "", ore: 1, tariffa }])} data-testid="add-manodopera-row-button" className="w-full border-dashed bg-white">
          <Plus className="mr-1 h-4 w-4" /> Manodopera a ore
        </Button>
      </div>
    </div>
  );
};
